<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import { useAnimatedElements, useImageDownloader, useMap, usePois, useGlobal } from '@/composables';
import type { FirstAnimationFrame, ZoneAnimation } from '@/types';

const { animations, frame } = useAnimatedElements();
const { isHighQuality, isRgb, isTransformationBeingApplied } = useMap();
const { poiFirstsFrames } = usePois();
const { handleLoadAsset, isFinished } = useImageDownloader();
const { isDragging } = useGlobal();

const urls: { [key: string]: URL } = {};

const getSpriteUrl = (name: string, quality: 'lq' | 'hq') => {
  const key = `animation-sprite-${name}-${quality}`;
  if (!(key in urls)) {
    urls[key] = new URL(
      `/src/assets/images/Animations/Animations_sprites_${quality === 'lq' ? '25' : '50'}/${name}.avif?url`,
      import.meta.url,
    );
  }

  return urls[key].href;
};

const getFirstUrl = (name: string, type: 'rgb' | 'bw') => {
  const key = `animation-first-${type}-${name}`;
  if (!(key in urls)) {
    urls[key] = new URL(
      `/src/assets/images/Animations/Firsts/${name}-first-${type}.avif?url`,
      import.meta.url,
    );
  }

  return urls[key].href;
};

const animationEls = ref<HTMLDivElement[]>([]);
const zoneEls = ref<HTMLDivElement[]>([]);
const restoreState = ref<boolean>(false); // recalculate sprite position after zoom transition
const spriteAnimations = ref<ZoneAnimation[]>([]);

const excludedAnimations = ref<number[]>([]);

const filteredAnimations = computed(() => {
  return spriteAnimations.value.filter((zone) => {
    const index = parseInt(zone[0].name.match(/\d+/)![0]);
    return !excludedAnimations.value.includes(index);
  });
});

const excludedAnimation = computed(() => {
  return spriteAnimations.value.filter((zone) => {
    return !filteredAnimations.value.includes(zone);
  });
});

onMounted(() => {
  spriteAnimations.value = animations.value.filter((zone) => zone[0].engine === 'sprite');
  spriteAnimations.value.forEach((zone) => {
    zone.forEach(() => {
      if (zone.length === 2) {
        zone[1].hidden = true;
      }
    });
  });
});

useIntersectionObserver(
  zoneEls,
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.target.children[0]) {
        return;
      }

      if (entry.isIntersecting) {
        entry.target.children[0].classList.remove('hidden');
      } else {
        entry.target.children[0].classList.add('hidden');
      }
    });
  },
  {
    immediate: true,
    threshold: 0,
  },
);

watch(isTransformationBeingApplied, (newValue) => {
  if (newValue) {
    restoreState.value = true;
  }
});

watch(frame, (newValue) => {
  filteredAnimations.value.forEach((zone) => {
    zone.forEach((animatedElement, index) => {
      if (zone.length === 2 && newValue === animatedElement.startingFrame) {
        zone[index].hidden = false;
        zone[(index + 1) % zone.length].hidden = true;
      }

      if (newValue >= animatedElement.startingFrame && newValue <= animatedElement.endingFrame) {
        const currentSpriteIndex = newValue - animatedElement.startingFrame;
        const currentRow = Math.floor(currentSpriteIndex / 12);

        const anim = animationEls.value.find((el) => el.dataset.name === animatedElement.name);

        if (!anim) return;

        const newLeft = `-${(currentSpriteIndex % 12) * animatedElement.width}px`;
        const newTop = `-${currentRow * animatedElement.height}px`;
        anim.style.transform = `translate3d(${newLeft}, ${newTop}, 0)`;
      } else if (restoreState.value) {
        const anim = animationEls.value.find((el) => el.dataset.name === animatedElement.name);

        if (!anim) return;

        if (newValue < animatedElement.startingFrame) {
          anim.style.transform = `translate3d(0px, 0px, 0)`;
        } else if (newValue > animatedElement.endingFrame) {
          const currentSpriteIndex = animatedElement.frames - 1; // last frame
          const currentRow = Math.floor(currentSpriteIndex / 12);

          const newLeft = `-${(currentSpriteIndex % 12) * animatedElement.width}px`;
          const newTop = `-${currentRow * animatedElement.height}px`;
          anim.style.transform = `translate3d(${newLeft}, ${newTop}, 0)`;
        }
      }
    });
  });

  if (restoreState.value) {
    restoreState.value = false;
  }
});
</script>

<template>
  <div data-img-preload v-if="!isFinished">
    <template v-for="(zone, index) in filteredAnimations" :key="index">
      <div
        v-for="(animation, animationIndex) in zone"
        :key="animation.name"
        class="absolute z-rgb-animations hidden"
        :id="animation.name"
      >
        <img
          :src="getSpriteUrl(animation.name, 'hq')"
          class="absolute z-rgb-animations hidden"
          @load="handleLoadAsset"
        />
        <img
          :src="getSpriteUrl(animation.name, 'lq')"
          class="absolute z-rgb-animations hidden"
          @load="handleLoadAsset"
        />
        <template v-if="animationIndex === 0">
          <img
            :src="getFirstUrl(animation.name, 'bw')"
            class="absolute z-rgb-animations hidden"
            @load="handleLoadAsset"
          />
          <img
            :src="getFirstUrl(animation.name, 'rgb')"
            class="absolute z-rgb-animations hidden"
            @load="handleLoadAsset"
          />
        </template>
      </div>
    </template>
  </div>
  <template v-if="isFinished && !isTransformationBeingApplied && !isDragging">
    <template v-for="(zone, index) in filteredAnimations" :key="index">
      <div
        v-for="animation in zone"
        :key="animation.name"
        :style="{
          transform: `translate3d(${animation.left}px, ${animation.top}px, 0)`,
          width: `${animation.width}px`,
          height: `${animation.height}px`,
        }"
        ref="zoneEls"
        class="pointer-events-none absolute z-rgb-animations origin-top-left select-none overflow-hidden"
        :id="animation.name"
      >
        <img
          v-show="isRgb && !animation.hidden && !restoreState"
          :src="getSpriteUrl(animation.name, isHighQuality ? 'hq' : 'lq')"
          :style="{
            width: `${animation.width * 12}px`,
            height: `${animation.height * animation.duration}px`,
          }"
          :data-name="animation.name"
          ref="animationEls"
          class="absolute z-rgb-animations max-w-none will-change-transform"
        />
      </div>
    </template>
  </template>
  <template v-for="(zone, index) in spriteAnimations" :key="index">
    <KeepAlive>
      <template v-if="!isRgb">
        <img
          v-show="
            !poiFirstsFrames.includes(`${zone[0].name}-first-rgb` as FirstAnimationFrame<'rgb'>)
          "
          class="pointer-events-none absolute z-bw-animations max-w-none origin-top-left select-none"
          :style="{
            transform: `translate3d(${zone[0].left}px, ${zone[0].top}px, 0)`,
            width: `${zone[0].width}px`,
            height: `${zone[0].height}px`,
          }"
          :src="getFirstUrl(zone[0].name, 'bw')"
          @load="!isFinished ? handleLoadAsset() : null"
          :alt="zone[0].alt"
        />
        <img
          v-show="
            poiFirstsFrames.includes(`${zone[0].name}-first-rgb` as FirstAnimationFrame<'rgb'>)
          "
          class="pointer-events-none absolute z-rgb-animations origin-top-left select-none"
          :style="{
            transform: `translate3d(${zone[0].left}px, ${zone[0].top}px, 0)`,
            width: `${zone[0].width}px`,
            height: `${zone[0].height}px`,
          }"
          :src="getFirstUrl(zone[0].name, 'rgb')"
          @load="!isFinished ? handleLoadAsset() : null"
          :alt="zone[0].alt"
        />
      </template>
      <template v-else>
        <img
          v-show="excludedAnimation.includes(zone)"
          class="pointer-events-none absolute z-rgb-animations origin-top-left select-none"
          :style="{
            transform: `translate3d(${zone[0].left}px, ${zone[0].top}px, 0)`,
            width: `${zone[0].width}px`,
            height: `${zone[0].height}px`,
          }"
          :src="getFirstUrl(zone[0].name, 'rgb')"
          @load="!isFinished ? handleLoadAsset() : null"
          :alt="zone[0].alt"
        />
      </template>
    </KeepAlive>
  </template>
</template>
